<template>
    <div
        class="kv-image-input"
        :class="[sizeClass, `kv-${variant}-input`, { 'kv-disabled': disabled, 'kv-error': hasError }]"
    >
        <label v-if="label" class="kv-label" :for="id">{{ label }}</label>

        <article v-if="normalizedImage" class="kv-image-input-item">
            <img
                v-if="normalizedImage.previewUrl"
                class="kv-image-input-preview"
                :src="normalizedImage.previewUrl"
                :alt="normalizedImage.name"
            />

            <div v-else class="kv-image-input-placeholder" aria-hidden="true"></div>

            <div class="kv-image-input-info">
                <strong>{{ normalizedImage.name }}</strong>
                <span
                    v-if="uploadStatus"
                    :class="['kv-image-input-status', `status-${uploadStatus}`]"
                >
                    {{ statusText }}
                </span>
            </div>

            <button
                class="kv-image-input-delete"
                type="button"
                :disabled="disabled"
                :aria-label="resolvedDeleteLabel"
                :title="resolvedDeleteLabel"
                @click="requestDelete"
            >
                <Icon icon-class="ui-trash-01" :size="calculatedIconSize" color="#f04438" />
            </button>
        </article>

        <KvFileDropzone
            v-else
            :accept="accept"
            :title="resolvedTitle"
            :description="resolvedDescription"
            :button-label="resolvedButtonLabel"
            :disabled="disabled"
            :variant="variant"
            :size="size"
            :has-error="hasError"
            @files="handleFiles"
        />

        <div
            v-if="deleteConfirmOpen"
            class="kv-image-input-modal-backdrop"
            @click.self="closeDeleteConfirm"
        >
            <KvCard
                class="kv-image-input-delete-modal"
                content-class="kv-image-input-delete-modal-content"
            >
                <template #text>
                    <h4>{{ t("inputs-image-deleteModalTitle") }}</h4>
                    <p>{{ t("inputs-image-deleteModalDescription") }}</p>

                    <div class="kv-image-input-delete-modal-actions">
                        <KvButton
                            type="button"
                            variant="outlined"
                            @click="closeDeleteConfirm"
                        >
                            {{ t("inputs-image-deleteModalCancel") }}
                        </KvButton>

                        <KvButton
                            class="kv-image-input-confirm-button"
                            type="button"
                            variant="outlined"
                            @click="confirmDelete"
                        >
                            {{ t("inputs-image-deleteModalConfirm") }}
                        </KvButton>
                    </div>
                </template>
            </KvCard>
        </div>
    </div>
</template>

<script>
import Icon from "../icon/icon.vue";
import KvButton from "../form/kv-button.vue";
import KvCard from "../ui/kv-card.vue";
import KvFileDropzone from "./kv-file-dropzone.vue";
import { uploadFilesToStorage } from "../../utils/upload-helper";

export default {
    name: "KvImageInput",
    components: {
        Icon,
        KvButton,
        KvCard,
        KvFileDropzone,
    },
    model: {
        prop: "value",
        event: "input",
    },
    props: {
        value: {
            type: Object,
            default: null,
        },
        label: {
            type: String,
            default: "",
        },
        id: {
            type: String,
            default() {
                return "kv-image-input-" + Math.random().toString(36).substr(2, 9);
            },
        },
        variant: {
            type: String,
            default: "outlined",
        },
        size: {
            type: String,
            default: "regular",
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        hasError: {
            type: Boolean,
            default: false,
        },
        accept: {
            type: String,
            default: "image/jpeg,image/png",
        },
        title: {
            type: String,
            default: "",
        },
        description: {
            type: String,
            default: "",
        },
        buttonLabel: {
            type: String,
            default: "",
        },
        deleteLabel: {
            type: String,
            default: "",
        },
        showNotifications: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            localFile: null,
            uploadStatus: null,
            deleteConfirmOpen: false,
        };
    },
    computed: {
        sizeClass() {
            return `kv-${this.size}-input`;
        },
        calculatedIconSize() {
            switch (this.size) {
                case "small":
                    return 2;
                case "large":
                    return 4;
                default:
                    return 3;
            }
        },
        normalizedImage() {
            if (this.localFile) return this.localFile;
            if (!this.value) return null;

            let fallbackName = this.t("inputs-image-defaultName");
            if (this.value.url) {
                const parts = this.value.url.split("/");
                fallbackName = parts[parts.length - 1].split("?")[0] || fallbackName;
            }

            return {
                ...this.value,
                name: this.value.name || fallbackName,
                previewUrl: this.value.previewUrl || this.value.url || "",
            };
        },
        statusText() {
            const statusMap = {
                completed: "inputs-image-statusCompleted",
                uploading: "inputs-image-statusUploading",
                error: "inputs-image-statusError",
            };

            if (!this.uploadStatus || !statusMap[this.uploadStatus]) return "";

            return this.t(statusMap[this.uploadStatus]);
        },
        resolvedTitle() {
            return this.title || this.t("inputs-image-title");
        },
        resolvedDescription() {
            return this.description || this.t("inputs-image-description");
        },
        resolvedButtonLabel() {
            return this.buttonLabel || this.t("inputs-image-button");
        },
        resolvedDeleteLabel() {
            return this.deleteLabel || this.t("inputs-image-delete");
        },
    },
    beforeDestroy() {
        this.revokeLocalPreviewUrl();
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        async handleFiles(files) {
            const file = files.find(this.isAcceptedImage);

            if (!file) return;

            this.revokeLocalPreviewUrl();
            this.localFile = {
                id: "local-" + Date.now() + "-" + file.name,
                name: file.name,
                previewUrl: URL.createObjectURL(file),
            };
            this.uploadStatus = "uploading";

            try {
                const urls = await uploadFilesToStorage([file], this.$root.t.bind(this.$root));

                if (urls && urls.length > 0) {
                    const uploadedUrl = urls[0];

                    this.$emit("input", {
                        id: "uploaded-" + Date.now() + "-" + file.name,
                        name: file.name,
                        previewUrl: uploadedUrl,
                        url: uploadedUrl,
                    });

                    this.localFile = null;
                    this.uploadStatus = "completed";

                    if (this.showNotifications) {
                        this.$store.dispatch("notification/success", {
                            text: this.t("inputs-image-success"),
                        });
                    }
                }
            } catch (error) {
                this.uploadStatus = "error";

                if (this.showNotifications) {
                    this.$store.dispatch("notification/error", error.message);
                }
            }
        },
        requestDelete() {
            if (this.disabled) return;
            this.deleteConfirmOpen = true;
        },
        closeDeleteConfirm() {
            this.deleteConfirmOpen = false;
        },
        confirmDelete() {
            this.revokeLocalPreviewUrl();
            this.localFile = null;
            this.uploadStatus = null;
            this.$emit("input", null);
            this.deleteConfirmOpen = false;

            if (this.showNotifications) {
                this.$store.dispatch("notification/success", {
                    text: this.t("inputs-image-deleted"),
                });
            }
        },
        revokeLocalPreviewUrl() {
            if (
                this.localFile &&
                this.localFile.previewUrl &&
                this.localFile.previewUrl.startsWith("blob:")
            ) {
                URL.revokeObjectURL(this.localFile.previewUrl);
            }
            this.localFile = null;
        },
        isAcceptedImage(file) {
            const acceptedTypes = this.accept.split(",").map((type) => type.trim());
            const acceptedExtensions = [".jpg", ".jpeg", ".png", ".webp"];
            const fileName = String(file.name || "").toLowerCase();

            return (
                acceptedTypes.includes(file.type) ||
                acceptedExtensions.some((extension) => fileName.endsWith(extension))
            );
        },
    },
};
</script>

<style lang="less">
.kv-image-input {
    display: flex;
    flex-direction: column;
    font-family: inherit;
    width: 100%;

    &.kv-disabled {
        opacity: 0.6;
        pointer-events: none;
    }

    .kv-label {
        margin-bottom: 6px;
        font-size: inherit;
    }
}

.kv-image-input-item {
    align-items: center;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    display: flex;
    gap: 8px;
    min-height: 76px;
    padding: 10px;
    width: 100%;
}

.kv-tinted-input .kv-image-input-item {
    background: #f3faf4;
    border-color: #2aaa4c;
}

.kv-error .kv-image-input-item {
    border-color: #f87171;
}

.kv-image-input-preview,
.kv-image-input-placeholder {
    border-radius: 6px;
    flex: 0 0 107px;
    height: 60px;
    object-fit: cover;
    width: 107px;
}

.kv-image-input-placeholder {
    background: #e5e7eb;
}

.kv-image-input-info {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 8px;
    justify-content: center;
    min-width: 0;

    strong {
        color: #1f2a37;
        font-family: "Sora", sans-serif;
        font-size: 16px;
        font-weight: 500;
        line-height: 1.3;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.kv-image-input-status {
    color: #6c737f;
    font-family: "Inter", sans-serif;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.2;

    &.status-completed {
        color: #17b26a;
    }

    &.status-error {
        color: #f04438;
    }
}

.kv-image-input-delete {
    align-items: center;
    appearance: none;
    background: #fee4e2;
    border: 0;
    border-radius: 6px;
    cursor: pointer;
    display: inline-flex;
    flex: 0 0 auto;
    height: 32px;
    justify-content: center;
    margin-left: auto;
    padding: 0;
    width: 32px;

    &:hover,
    &:focus {
        background: #fecdca;
        outline: none;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
}

.kv-image-input-modal-backdrop {
    align-items: center;
    background: rgba(17, 25, 39, 0.72);
    bottom: 0;
    display: flex;
    justify-content: center;
    left: 0;
    padding: 16px;
    position: fixed;
    right: 0;
    top: 0;
    z-index: 1050;
}

.kv-image-input-delete-modal {
    max-width: 520px;
    width: 100%;
}

.kv-image-input-delete-modal-content {
    padding: 16px;
}

.kv-image-input-delete-modal h4 {
    color: #4d5761;
    font-family: "Sora", sans-serif;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.3;
    margin: 0 0 12px;
}

.kv-image-input-delete-modal p {
    color: #384250;
    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.3;
    margin: 0;
}

.kv-image-input-delete-modal-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 24px;
}

.kv-image-input-confirm-button {
    background: #f04438 !important;
    border-color: #f04438 !important;
    color: #fff !important;
}

@media (max-width: 480px) {
    .kv-image-input-item {
        align-items: flex-start;
    }

    .kv-image-input-preview,
    .kv-image-input-placeholder {
        flex-basis: 82px;
        height: 48px;
        width: 82px;
    }

    .kv-image-input-info strong {
        white-space: normal;
        overflow-wrap: anywhere;
    }

    .kv-image-input-delete-modal-actions {
        flex-direction: column;
    }
}
</style>
